import { AppIcon, AppIcons } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, SxProps, Theme, Stack, Typography } from '@mui/material';

import { VisibleStep, OnboardingStepKey } from './utils/getVisibleSteps';
import { StepKey, STEP_TO_SIDEBAR_INDEX } from './utils/stepNavigation';

const SIDEBAR_STRINGS = appStrings.modules.onboarding.sidebar;

type SidebarItem = Readonly<{
  title: string;
  description: string;
  iconName: AppIcons;
}>;

const SIDEBAR_ITEMS: SidebarItem[] = [
  {
    title: SIDEBAR_STRINGS.createAccount,
    description: 'Name, email, password',
    iconName: 'CursorClick01',
  },
  {
    title: SIDEBAR_STRINGS.yourRole,
    description: 'Are you a student or a parent?',
    iconName: 'User01',
  },
  {
    title: SIDEBAR_STRINGS.selectYourPlan,
    description: 'Activate your account',
    iconName: 'Tag01',
  },
  {
    title: SIDEBAR_STRINGS.inviteFamily,
    description: 'Up to 6 other members',
    iconName: 'Mail05',
  },
];

// Map sidebar items to the first onboarding step key in that section
// Index 0 ("Create account") is the signup page, not navigable during onboarding
const SIDEBAR_TO_STEP_KEY_MAP: Record<number, OnboardingStepKey | null> = {
  0: null, // Create account - not navigable (signup page)
  1: 'ROLE_SELECTION', // Your role section
  2: 'ACTIVATION_CODE', // Select your plan section
  3: 'INVITE_FAMILY', // Invite family section
};

type OnboardingSidebarItemProps = Readonly<{
  lastItemIndex: number;
  index: number;
  item: SidebarItem;
  selectedIndex: number;
  isClickable: boolean;
  onClick?: () => void;
}>;

const OnboardingSidebarItem = ({
  lastItemIndex,
  index,
  item,
  selectedIndex,
  isClickable,
  onClick,
}: OnboardingSidebarItemProps) => {
  return (
    <Box
      key={item.title}
      id={`onboarding-sidebar-item-${index}`}
      sx={(theme) => sidebarItemContainerSx(theme, selectedIndex === index, isClickable)}
      onClick={isClickable ? onClick : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={(e) => {
        if (isClickable && onClick && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box sx={iconContainerSx}>
          <AppIcon diameter="24px" iconName={item.iconName} color={'Colors/Text/text-primary'} />
        </Box>
        {/* divider */}
        {lastItemIndex !== index && <Box sx={dividerSx}></Box>}
      </Box>
      <Stack>
        <Typography
          color={(theme) => theme.backrs.colors['Colors/Text/text-secondary']}
          variant="text-md-semibold"
        >
          {item.title}
        </Typography>
        <Typography
          color={(theme) => theme.backrs.colors['Colors/Text/text-tertiary']}
          variant="text-md-regular"
        >
          {item.description}
        </Typography>
      </Stack>
    </Box>
  );
};

type OnboardingSidebarItemsProps = Readonly<{
  currentStep: StepKey;
  isShowingAccountCreationLoading: boolean;
  isShowingInviteFamily: boolean;
  showInviteFamily: boolean;
  visibleSteps: VisibleStep[];
  currentProgressStepIndex: number;
  canNavigateBack: boolean;
  onProgressStepClick: (stepIndex: number) => void;
}>;

export function OnboardingSidebarItems({
  currentStep,
  isShowingAccountCreationLoading,
  isShowingInviteFamily,
  showInviteFamily,
  visibleSteps,
  currentProgressStepIndex,
  canNavigateBack,
  onProgressStepClick,
}: OnboardingSidebarItemsProps) {
  // Filter sidebar items based on feature flag
  const sidebarItems = showInviteFamily
    ? SIDEBAR_ITEMS
    : SIDEBAR_ITEMS.filter((item) => item.title !== 'Invite family members');

  // Map the current step to the correct sidebar item index
  const getMappedSelectedIndex = (): number => {
    // Account creation loading is past all steps
    if (isShowingAccountCreationLoading) {
      return sidebarItems.length;
    }

    // Invite family view maps to the invite family sidebar item
    if (isShowingInviteFamily) {
      return 3;
    }

    const mappedIndex = STEP_TO_SIDEBAR_INDEX[currentStep];

    // If invite family is hidden and mapped index would be 3 (invite family), cap at last item
    if (!showInviteFamily && mappedIndex >= sidebarItems.length) {
      return sidebarItems.length - 1;
    }

    return mappedIndex;
  };

  const mappedSelectedIndex = getMappedSelectedIndex();

  // Check if a sidebar item is clickable (can navigate back to it)
  const isSidebarItemClickable = (sidebarIndex: number): boolean => {
    if (!canNavigateBack) return false;

    const stepKey = SIDEBAR_TO_STEP_KEY_MAP[sidebarIndex];

    if (!stepKey) return false; // Not a navigable item (e.g., "Create account")

    // Find the corresponding progress step
    const progressStepIndex = visibleSteps.findIndex((step) => step.key === stepKey);

    if (progressStepIndex < 0) return false; // Step not visible

    // Only clickable if it's before the current step
    return progressStepIndex < currentProgressStepIndex;
  };

  // Handle sidebar item click - map to progress step and call handler
  const handleSidebarItemClick = (sidebarIndex: number) => {
    const stepKey = SIDEBAR_TO_STEP_KEY_MAP[sidebarIndex];

    if (!stepKey) return;

    const progressStepIndex = visibleSteps.findIndex((step) => step.key === stepKey);

    if (progressStepIndex >= 0) {
      onProgressStepClick(progressStepIndex);
    }
  };

  return (
    <Box>
      {sidebarItems.length > 0 &&
        sidebarItems.map((item, index) => (
          <OnboardingSidebarItem
            key={item.title}
            item={item}
            lastItemIndex={sidebarItems.length - 1}
            index={index}
            selectedIndex={mappedSelectedIndex}
            isClickable={isSidebarItemClickable(index)}
            onClick={() => handleSidebarItemClick(index)}
          />
        ))}
    </Box>
  );
}

const dividerSx: SxProps<Theme> = ({ backrs }) => ({
  width: '2px',
  height: '26px',
  borderRadius: backrs.radius['radius-xxs'],
  backgroundColor: backrs.colors['Gray/200'],
  marginY: backrs.spacing['spacing-xs'],
});

const sidebarItemContainerSx = (theme: Theme, isSelected: boolean, isClickable: boolean) => ({
  display: 'flex',
  gap: theme.backrs.spacing['spacing-xl'],
  opacity: isSelected ? 1 : '0.6',
  transition: 'opacity 300ms ease-in-out',
  userSelect: 'none',
  cursor: isClickable ? 'pointer' : 'default',
  '&:hover': isClickable
    ? {
        opacity: 0.8,
      }
    : {},
  '&:focus-visible': {
    outline: `2px solid ${theme.backrs.colors['Colors/Foreground/fg-brand-primary-alt']}`,
    outlineOffset: '2px',
    borderRadius: theme.backrs.radius['radius-sm'],
  },
});

const iconContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  borderRadius: backrs.radius['radius-lg'],
  border: `1px solid ${backrs.colors['Gray/400']}`,
  padding: '12px',
  backgroundColor: backrs.colors['Colors/Base/White'],
  justifyContent: 'center',
  alignItems: 'center',
});
